import type ReactAce from 'react-ace';
import { ReactNode, useRef } from 'react';
import { useIntl } from 'react-intl';
import { KeyboardShortcut } from '@rossum/components/keyboard-shortcut/KeyboardShortcut';
import { IconArrowBackUp, IconArrowForwardUp } from '@rossum/ui/icons/tabler';
import {
  alpha,
  Divider,
  IconButton,
  Stack,
  SvgIcon,
  Tooltip,
} from '@rossum/ui/material';

type Props = {
  editor: ReactAce;
  renderButtons?: ({ editor }: { editor: ReactAce['editor'] }) => ReactNode;
};

const FormulaEditorToolbar = (props: Props) => {
  const intl = useIntl();
  const { editor } = props.editor;
  const undoManager = useRef(editor.session.getUndoManager());
  const undoDisabled = !undoManager.current.hasUndo();
  const redoDisabled = !undoManager.current.hasRedo();

  const withFocusHandler = (action: () => void) => () => {
    action();
    editor.focus();
  };

  return (
    <Stack
      sx={{
        backgroundColor: theme => alpha(theme.palette.action.active, 0.05),
        borderBottom: '1px solid',
        borderBottomColor: 'other.muted',
        py: 1,
        px: 2,
      }}
      direction="row"
      justifyContent="space-between"
    >
      <Stack
        direction="row"
        divider={<Divider orientation="vertical" flexItem />}
        spacing={2}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip
            title={
              undoDisabled ? (
                ''
              ) : (
                <KeyboardShortcut
                  keyName="ctrlZ"
                  description={intl.formatMessage({
                    id: 'containers.settings.fields.edit.formula.toolbar.undo.tooltip',
                  })}
                />
              )
            }
          >
            <IconButton
              size="small"
              onClick={withFocusHandler(() => editor.undo())}
              disabled={undoDisabled}
            >
              <SvgIcon fontSize="medium">
                <IconArrowBackUp />
              </SvgIcon>
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              redoDisabled ? (
                ''
              ) : (
                <KeyboardShortcut
                  keyName="ctrlShiftZ"
                  description={intl.formatMessage({
                    id: 'containers.settings.fields.edit.formula.toolbar.redo.tooltip',
                  })}
                />
              )
            }
          >
            <IconButton
              size="small"
              onClick={withFocusHandler(() => editor.redo())}
              disabled={redoDisabled}
            >
              <SvgIcon fontSize="medium">
                <IconArrowForwardUp />
              </SvgIcon>
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      {props.renderButtons?.({ editor }) ?? null}
    </Stack>
  );
};

export { FormulaEditorToolbar };
